import { useEffect } from "react";

/**
 * Remove focus from input elements when the user scrolls
 *
 * This fixes an issue with mobile devices where virtual keyboard being shown
 * causes fixed/sticky elements to not work correctly.
 *
 * By removing focus from the input element, the virtual keyboard will not be shown.
 *
 * Uses `touchmove` as a gate to distinguish real user scrolls from
 * iOS-initiated viewport-adjustment scrolls (e.g. keyboard open/close).
 * @param enabled - Whether to enable the scroll blur
 */
export function useScrollBlur(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;

    let userIsScrolling = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    function handleTouchMove() {
      userIsScrolling = true;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        userIsScrolling = false;
      }, 150);
    }

    function handleScroll() {
      if (!userIsScrolling) return;
      if (document.activeElement instanceof HTMLInputElement) {
        document.activeElement.blur();
      }
    }

    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true });

    return function dispose() {
      clearTimeout(timeoutId);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [enabled]);
}
